import { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import type { Editor } from '@tiptap/react';
import {
  BoldIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  UnderlineIcon,
  Unlink2Icon,
} from 'lucide-react';
import { looksLikeRichTextHtml } from '@kennisbank/shared';
import { markdownToHtml } from '../lib/markdown-to-html.js';
import { toSafeHref } from '../lib/link-href.js';
import { PROSE } from './RichText.js';

/**
 * Tekstveld met opmaak. Selecteer tekst en er verschijnt een balkje met vet,
 * cursief, onderstreept, lijsten en een linkknop.
 *
 * Slaat HTML op. Bestaande markdown of platte tekst wordt bij het openen
 * omgezet (zie markdownToHtml), zodat er niets aan de database gemigreerd hoeft
 * te worden: pas bij de eerstvolgende opslag wordt een veld HTML.
 *
 * De HTML die hier uitkomt wordt aan de serverkant gefilterd
 * (backend/src/services/rich-text.sanitize.ts) — nooit vertrouwen op de editor
 * als grens, want een aanvaller praat rechtstreeks met de API.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 'min-h-[160px]',
  html,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
  /** Veld bevat per definitie HTML — sla de markdown-omzetting over. Zie RichText. */
  html?: boolean;
}): JSX.Element {
  const editor = useEditor({
    // Zonder deze staat de knopbalk de vorige selectie te tonen: TipTap v3
    // hertekent standaard alleen bij een document-wijziging, niet bij het
    // enkel verplaatsen van de selectie.
    shouldRerenderOnTransaction: true,
    extensions: [
      // Afbeeldingen en tabellen zitten NIET in StarterKit. Zonder deze twee
      // gooit ProseMirror ze bij het laden weg als onbekende nodes, en wist de
      // eerstvolgende opslag ze uit bestaande lessen — zonder waarschuwing.
      Image,
      Table,
      TableRow,
      TableHeader,
      TableCell,
      StarterKit.configure({
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        },
      }),
    ],
    content: toHtml(value, html),
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? '' : e.getHTML()),
    editorProps: {
      attributes: {
        class: `${PROSE} ${minHeight} w-full rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[13px] text-ink-900 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400`,
      },
    },
  });

  // Inhoud die ná het monteren binnenkomt (bijv. een fetch die later landt).
  // Alleen terugschrijven als de editor het niet zelf al bevat, anders springt
  // de cursor naar het begin bij elke toetsaanslag.
  useEffect(() => {
    if (!editor) return;
    const next = toHtml(value, html);
    if (next !== editor.getHTML() && (editor.isEmpty || value === '')) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [editor, value, html]);

  if (!editor) return <div className={`${minHeight} rounded-lg bg-brand-50/50`} />;

  return (
    <div className="relative">
      <BubbleMenu editor={editor}>
        <div className="flex items-center gap-0.5 rounded-xl border border-brand-100 bg-white p-1 shadow-lg">
          <ToolbarButton
            editor={editor}
            label="Vet"
            active="bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon size={14} />
          </ToolbarButton>
          <ToolbarButton
            editor={editor}
            label="Cursief"
            active="italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <ItalicIcon size={14} />
          </ToolbarButton>
          <ToolbarButton
            editor={editor}
            label="Onderstreept"
            active="underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon size={14} />
          </ToolbarButton>

          <div className="mx-0.5 h-5 w-px bg-brand-100" />

          <ToolbarButton
            editor={editor}
            label="Opsomming"
            active="bulletList"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <ListIcon size={14} />
          </ToolbarButton>
          <ToolbarButton
            editor={editor}
            label="Genummerde lijst"
            active="orderedList"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrderedIcon size={14} />
          </ToolbarButton>

          <div className="mx-0.5 h-5 w-px bg-brand-100" />

          <ToolbarButton
            editor={editor}
            label="Link plaatsen"
            active="link"
            onClick={() => promptForLink(editor)}
          >
            <LinkIcon size={14} />
          </ToolbarButton>
          {editor.isActive('link') && (
            <ToolbarButton
              editor={editor}
              label="Link verwijderen"
              onClick={() => editor.chain().focus().unsetLink().run()}
            >
              <Unlink2Icon size={14} />
            </ToolbarButton>
          )}
        </div>
      </BubbleMenu>

      <EditorContent editor={editor} />

      {editor.isEmpty && placeholder && (
        <p className="pointer-events-none absolute left-3 top-2 text-[13px] text-ink-900/35">
          {placeholder}
        </p>
      )}
    </div>
  );
}

function ToolbarButton({
  editor,
  label,
  active,
  onClick,
  children,
}: {
  editor: Editor;
  label: string;
  active?: string;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  const isActive = active ? editor.isActive(active) : false;
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`rounded-lg p-1.5 transition-colors ${
        isActive ? 'bg-brand-600 text-white' : 'text-ink-900/70 hover:bg-brand-50'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Link plaatsen via window.prompt.
 *
 * ponytail: een eigen link-popover is mooier maar is een tweede focus-val
 * bovenop de bubble menu; prompt() werkt en kost nul regels state. Vervang dit
 * zodra iemand vraagt om een link te kunnen bewerken in plaats van opnieuw te
 * plaatsen.
 */
function promptForLink(editor: Editor): void {
  const current = (editor.getAttributes('link').href as string | undefined) ?? '';
  const input = window.prompt('Link (bijv. https://suppohrt.nl)', current);
  if (input === null) return;
  if (!input.trim()) {
    editor.chain().focus().unsetLink().run();
    return;
  }
  const href = toSafeHref(input);
  if (!href) {
    window.alert('Dat is geen geldige link. Gebruik een webadres of een e-mailadres.');
    return;
  }
  editor.chain().focus().setLink({ href }).run();
}

function toHtml(value: string, isHtml?: boolean): string {
  if (!value) return '';
  return isHtml === true || looksLikeRichTextHtml(value) ? value : markdownToHtml(value);
}
